<template>
  <v-container fluid v-if="show">
    <v-dialog v-model="show" max-width="800px" scrollable>
      <v-card>
        <v-card-title class="secondary white--text d-flex align-center justify-space-between">
          <span class="header-font" style="font-size: 22px;">
            {{ title }}
          </span>
        </v-card-title>
        <v-card-subtitle class="secondary py-2 white--text d-flex align-center justify-space-between">
          <span class="subheader-font body-2">{{ subtitle }}</span>
        </v-card-subtitle>
        <v-card-text class="py-0">
          <!--Clear / Select All-->
          <section class="d-flex justify-center mt-5">
            <v-btn small
              color="secondary"
              rounded outlined class="elevation-0 monospace-font px-4 me-2"
              @click="onClearClick()"
            >{{ $t('general.clear') }}</v-btn>
            <v-btn small color="secondary"
              rounded class="elevation-0 monospace-font white--text px-4"
              @click="onSelectAllClick()"
            >{{ $t('finishingMap.selectAll') }}</v-btn>
          </section>
          <!--Vertical Pitch-->
          <FieldAreaSelector v-if="isVertical" :areas="currentUserAreas" :fieldColor="currentUserFieldColor"
            @areaToggle="onAreaToggle($event)" ref="fieldAreaSelectorRef" :isVertical="true"></FieldAreaSelector>
          <!--Horizontal Pitch-->
          <FieldAreaSelector v-if="isHorizontal || (!isVertical && !isFullWidth && !isGoal)" :areas="currentUserAreas" :fieldColor="currentUserFieldColor"
            @areaToggle="onAreaToggle($event)" ref="fieldAreaSelectorRef"></FieldAreaSelector>
          <!--Full Width Pitch-->
          <FieldAreaSelector v-if="isFullWidth" :areas="currentUserAreas" :fieldColor="currentUserFieldColor"
            @areaToggle="onAreaToggle($event)" ref="fieldAreaSelectorRef"></FieldAreaSelector>
          <!--Goal Pitch-->
          <v-img v-if="isGoal"
            :src="require(`@/assets/img/front_goal.png`)"
            contain class="my-5"
            :style="{position: 'relative', width: '100%', aspectRatio: '2 / 1'}"
            ref="pitchImg"
          >
            <div v-for="area in goalAreas" :key="area.id">
              <v-tooltip top color="lightSecondary lighten-4" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-on="on" v-bind="attrs"
                    :key="area.id"
                    :color="area.selected ? 'primary' : 'grey lighten-2'"
                    class="area-toggle"
                    :style="getButtonStyle(area)"
                    @click="toggleGoalArea(area.id)"
                  ></v-btn>
                </template>
                <span class="black--text body-1">{{ area.label }}</span>
              </v-tooltip>
            </div>
          </v-img>
          <!--Selected Areas-->
          <section class="d-flex align-center justify-start flex-wrap" v-if="selectedAreas.length">
            <span class="body-1 font-weight-bold primary--text ms-2">Selected areas: </span>
            <v-chip v-for="(area, i) in selectedAreas" :key="i" small color="lightSecondary lighten-2" class="body-1 ma-1 black--text">
              <span class="font-weight-medium">{{ area.label || $t(`videoHighlights.areas.${area.name}`) }}</span>
            </v-chip>
          </section>
        </v-card-text>
        <v-card-actions class="pr-6 pb-4 d-flex justify-end">
          <v-btn
            :color="$vuetify.theme.dark ? 'white' : 'secondary darken-1'"
            rounded outlined class="elevation-0 monospace-font px-4"
            @click="onCancelClick()"
          >{{$t('general.cancel')}}</v-btn>
          <v-btn
            rounded class="elevation-0 monospace-font white--text px-4" color="primary"
            @click="onConfirmClick()"
          >{{ $t('general.confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>

import FieldAreaSelector from '../base/FieldAreaSelector.vue';
import GeneralService from '../../services/general.service';

export default {
  name: 'AreaSelectorModal',
  components:{
    FieldAreaSelector
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    isVertical: {
      type: Boolean,
      default: false
    },
    isHorizontal: {
      type: Boolean,
      default: false
    },
    isFullWidth: {
      type: Boolean,
      default: false
    },
    isGoal: {
      type: Boolean,
      default: false
    },
    areaModel: {
      type: Array,
      default: () => [],
      required: true
    }
  },
  data() {
    return {
      show: false,
      areaSelection:[],
      areas: [],
      goalAreas: [
        { id: 1, code: 'highLeft', label: this.$t("finishingMap.highLeft"), xPercent: 35, yPercent: 49.5, widthPercent: 14.6, heightPercent: 14.5, selected: false  },
        { id: 2, code: 'highCenter', label: this.$t("finishingMap.highCenter"), xPercent: 50.16, yPercent: 49.5, widthPercent: 14.6, heightPercent: 14.5, selected: false  },
        { id: 3, code: 'highRight', label: this.$t("finishingMap.highRight"), xPercent: 65.3, yPercent: 49.5, widthPercent: 14.6, heightPercent: 14.5, selected: false  },
        { id: 4, code: 'lowLeft', label: this.$t("finishingMap.lowLeft"), xPercent: 35, yPercent: 65, widthPercent: 14.6, heightPercent: 14.5, selected: false  },
        { id: 5, code: 'lowCenter', label: this.$t("finishingMap.lowCenter"), xPercent: 50.16, yPercent: 65, widthPercent: 14.6, heightPercent: 14.5, selected: false  },
        { id: 6, code: 'lowRight', label: this.$t("finishingMap.lowRight"), xPercent: 65.3, yPercent: 65, widthPercent: 14.6, heightPercent: 14.5, selected: false  },
        { id: 7, code: 'outHighLeft', label: this.$t("finishingMap.outHighLeft"), xPercent: 14.25, yPercent: 10.5, widthPercent: 23.5, heightPercent: 18.5, selected: false  },
        { id: 8, code: 'high', label: this.$t("finishingMap.high"), xPercent: 50, yPercent: 10.5, widthPercent: 47, heightPercent: 18.5, selected: false  },
        { id: 9, code: 'outHighRight', label: this.$t("finishingMap.outHighRight"), xPercent: 86, yPercent: 10.5, widthPercent: 23.5, heightPercent: 18.5, selected: false  },
        { id: 10, code: 'closeHighLeft', label: this.$t("finishingMap.closeHighLeft"), xPercent: 14.25, yPercent: 30, widthPercent: 23.5, heightPercent: 18.5, selected: false  },
        { id: 11, code: 'closeHigh', label: this.$t("finishingMap.closeHigh"), xPercent: 50, yPercent: 30, widthPercent: 47, heightPercent: 18.5, selected: false  },
        { id: 12, code: 'closeHighRight', label: this.$t("finishingMap.closeHighRight"), xPercent: 86, yPercent: 30, widthPercent: 23.5, heightPercent: 18.5, selected: false  },
        { id: 13, code: 'closeLeft', label: this.$t("finishingMap.closeLeft"), xPercent: 14.25, yPercent: 56.5, widthPercent: 23.5, heightPercent: 31.5, selected: false  },
        { id: 14, code: 'closeRight', label: this.$t("finishingMap.closeRight"), xPercent: 86, yPercent: 56.5, widthPercent: 23.5, heightPercent: 31.5, selected: false  },
      ],
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentUserFieldColor() {
      return this.$store.getters["auth/fieldColor"];
    },
    currentUserAreas(){
      this.areaSelection;
      let layoutId = this.$store.getters["auth/fieldLayout"]
      return _.chain(this.layouts).find(l => l.id === layoutId).get("areas").reduce((allAreas,a) => {
        a.selected = this.selectedAreasId.includes(a.id);
        let ownArea = {...a}
        ownArea.id = this.isFullWidth ? -ownArea.id : ownArea.id
        ownArea.x1 = 100 - (a.x2)
        ownArea.x2 = 100 - (a.x1)
        ownArea.y1 = this.isFullWidth ? a.y1 : 100 - (a.y2)
        ownArea.y2 = this.isFullWidth ? a.y2 : 100 - (a.y1)
        ownArea.name = this.isFullWidth ? `OWN ${ownArea.name}` : `${ownArea.name}`;
        ownArea.selected = this.selectedAreasId.includes(ownArea.id);
        if(!this.isVertical){
          allAreas.push(ownArea);
        }
        allAreas.push(a);
        return allAreas;
      },[]).value();
    },
    selectedAreas() {
      this.areaSelection;
      let target = this.isFullWidth || this.isHorizontal || this.isVertical ? this.areaSelection : this.areas;
      return _.chain(target).filter(a => a.selected && a.id < 100).uniqBy("id").value();
    },
    selectedAreasId() {
      return this.selectedAreas.map(a => a.id);
    },
    layouts(){
      return this.$store.getters["general/fieldLayouts"];
    }
  },
  created() {},
  mounted() {
  },
  methods: {
    async open() {
      if (this.isGoal) { 
        this.areas = this.goalAreas;
      }
      //Set selected areas
      if (this.areaModel.length) {
        if(this.isFullWidth || this.isHorizontal || this.isVertical){
          this.areaSelection = _.map(this.currentUserAreas,(a) =>{
            a.selected = this.areaModel.includes(a.id);
            return a;
          })
        }else{
          this.areas.forEach(area => {
            if (this.areaModel.includes(area.id) || this.areaModel.includes(area.code) || this.areaModel.includes(area.parentId)) {
              area.selected = true;
            };
          });
        }
      };
      this.show = true;
    },
    close() {
      this.areas = [];
      this.show = false;
    },
    onCancelClick() {
      this.close();
    },
    toggleGoalArea(areaId) {
      const area = this.goalAreas.find(a => a.id === areaId);
      if (area) area.selected = !area.selected;
    },
    getButtonStyle(area) {
      const containerWidth = window.innerWidth;
      let scaleFactor = 1;

      if (containerWidth < 850) scaleFactor = 0.6;
      else if (containerWidth < 1200) scaleFactor = 0.8;

      const width = area.widthPercent * scaleFactor;
      const height = area.heightPercent * scaleFactor;

      return {
        position: 'absolute',
        left: `${area.xPercent}%`,
        top: `${area.yPercent}%`,
        transform: 'translate(-50%, -50%)',
        width: `${width}%`,
        height: `${height}%`,
        minWidth: '10px',
        minHeight: '10px',
        opacity: 0.5,
      };
    },
    onConfirmClick() {
      const selectedAreasObj = { ids: this.selectedAreas.map(a => a.id), codes: this.selectedAreas.map(a => a.code || a.name) };
      this.$emit("onConfirmClick", selectedAreasObj);
      this.close();
    },
    onClearClick() {
      if(this.isFullWidth || this.isHorizontal || this.isVertical){
        this.$refs.fieldAreaSelectorRef.applyStateAll(false);
      }else{
        this.areas.forEach(area => area.selected = false);
      }
    },
    onSelectAllClick() {
      if(this.isFullWidth || this.isHorizontal || this.isVertical){
        this.$refs.fieldAreaSelectorRef.applyStateAll(true);
      }else{
        this.areas.forEach(area => area.selected = true);
      }
    },
    async loadLayouts(){
      let backResponse = await GeneralService.getFieldLayouts();
      this.layouts = backResponse.data;
    },
    onAreaToggle(areas){
      this.areaSelection = [...areas];
      this.$forceUpdate();
    }
  },
};
</script>

<style scoped>
  .area-toggle.v-btn {
    padding: 0;
    min-width: 10px !important;
    min-height: 10px !important;
  }
</style>



// WEBPACK FOOTER //
// src/views/modals/AreaSelectorModal.vue